import { Formik } from "formik";
import React, { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Creators as BankAccountFarmActions } from "../../../store/ducks/bankAccountFarm";

// Components
import { Col, DatePicker, Input, Row, Select, Spin } from "antd";
import ButtonStandard from "../../utils/button";
import { Container, Footer } from "./styles";

// Services
import { RadioButtonCustom, RadioGroupCustom } from "../supplier/styles";
import { indexAllBanks } from "../../../services/bankService";
import NumberFormat from "react-number-format";
import moment from "moment";
import RadioGroup from "antd/lib/radio/group";
import RadioButton from "../../utils/radioButton";

const INITIAL_FORM_STATE = {
  id: null,
  groupId: null,
  farmId: null,
  bankId: null,
  type: "PESSOA_FISICA",
  description: null,
  bankAccountNumber: null,
  bankAgencyNumber: null,
  initialBalanceDate: null,
  initialBalance: null,
  status: "Active",
};

const validationSchema = Yup.object({
  bankId: Yup.string().trim().required(),
  description: Yup.string().trim().required(),
  bankAccountNumber: Yup.number().required(),
  bankAgencyNumber: Yup.number().required(),
  initialBalanceDate: Yup.string().required(),
  initialBalance: Yup.number().required(),
});

const DrawerBankAccountFarm = () => {
  const formRef = useRef(null);
  const [banks, setBanks] = useState([]);

  // Redux Variable
  const {
    translation,
    groupSelected: { id: groupId },
    groupSelected,
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const {
    isBankAccountFarmDrawerVisible: isDrawerVisible,
    loadingBankAccountFarmTableData: isLoadingRequest,
    bankAccountFarmData: data,
  } = useSelector((state) => state.bankAccountFarm);

  const dispatch = useDispatch();

  // Method

  const getActiveBanks = useCallback(async () => {
    try {
      const {
        data: { results },
      } = await indexAllBanks({
        groupId,
        farmId,
      });
      setBanks(results);
    } catch (error) {}
  }, [farmId, groupId]);

  function handleCloseDrawer() {
    formRef.current.resetForm(INITIAL_FORM_STATE);
    dispatch(BankAccountFarmActions.switchDrawerBankAccountFarmVisibility());
  }

  async function handleSubmitForm(values) {
    try {
      dispatch(
        BankAccountFarmActions.saveBankAccountFarm(
          groupId,
          farmId,
          values.id,
          values
        )
      );
    } catch (error) {}
  }

  function handleDrawerVisible(visible) {
    if (visible) {
      getActiveBanks();
      if (data === null) {
        formRef.current.resetForm({
          ...INITIAL_FORM_STATE,
          groupId: groupId,
          farmId: farmId,
        });
      } else {
        formRef.current.resetForm(data);
      }
    }
  }

  return (
    <Container
      title={
        data?.id == null
          ? translation.bankAccountFarm.form.titleCreateNew
          : translation.bankAccountFarm.form.titleEdit
      }
      width={700}
      onClose={handleCloseDrawer}
      maskClosable={false}
      visible={isDrawerVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={isLoadingRequest}>
        <Formik
          ref={formRef}
          enableReinitialize={true}
          initialValues={INITIAL_FORM_STATE}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="drawerForm">
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                {/* Group And Farm Select visibility */}
                <Row type="flex" align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>
                        {translation.bankAccountFarm.form.groupFarmVisibility}
                      </label>
                    </Row>
                    <Row>
                      <RadioGroupCustom
                        value={
                          values.groupId != null && values.farmId != null
                            ? "farm"
                            : values.groupId != null && values.farmId == null
                            ? "group"
                            : "farm"
                        }
                        disabled={values.id}
                        buttonStyle="solid"
                      >
                        <RadioButtonCustom
                          value="farm"
                          onChange={(e) => {
                            setFieldValue("groupId", groupId);
                            setFieldValue("farmId", farmId);
                          }}
                        >
                          {translation.radiogroup.farm}
                        </RadioButtonCustom>
                        <RadioButtonCustom
                          value="group"
                          onChange={(e) => {
                            setFieldValue("groupId", groupId);
                            setFieldValue("farmId", null);
                            setFieldValue("supplierId", null);
                            setFieldValue("unitId", null);
                          }}
                        >
                          {translation.radiogroup.group}: {groupSelected.name}
                        </RadioButtonCustom>
                      </RadioGroupCustom>
                    </Row>
                  </Col>
                </Row>
                {/* Name */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.description && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.bankAccountFarm.form.description}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="description"
                    value={values.description}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    maxLength={50}
                    onChange={(e) => {
                      setFieldValue("description", e.target.value);
                    }}
                  />
                </Row>
                {/* Bank Select */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.bankId && submitCount > 0 ? "error" : ""}
                  >
                    {translation.bankAccountFarm.form.bankId}*
                  </label>
                </Row>
                <Row type="flex">
                  <Select
                    name="bankId"
                    value={values.bankId || undefined}
                    placeholder={translation.defaultSelectPlaceholder}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) => setFieldValue("bankId", value)}
                    style={{ width: "100%" }}
                  >
                    {banks.map((bank) => (
                      <Select.Option key={bank.id} value={bank.id}>
                        {bank.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Row>
                {/* Bank Type */}
                <Row type="flex" className="rowLabel">
                  <label>{translation.bankAccountFarm.form.type}</label>
                </Row>
                <Row type="flex">
                  <RadioGroup
                    name="type"
                    value={values.type || undefined}
                    onChange={(e) => {
                      setFieldValue("type", e.target.value);
                    }}
                  >
                    <RadioButton value={"PESSOA_FISICA"}>
                      {translation.bankAccountFarm.form.types["PESSOA_FISICA"]}
                    </RadioButton>
                    <RadioButton value={"PESSOA_JURIDICA"}>
                      {
                        translation.bankAccountFarm.form.types[
                          "PESSOA_JURIDICA"
                        ]
                      }
                    </RadioButton>
                  </RadioGroup>
                </Row>
                {/* Bank Account Number */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.bankAccountNumber && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.bankAccountFarm.form.bankAccountNumber}*
                  </label>
                </Row>
                <Row type="flex">
                  <NumberFormat
                    customInput={Input}
                    name="bankAccountNumber"
                    value={values.bankAccountNumber || ""}
                    placeholder={translation.defaultPlaceholder}
                    allowLeadingZeros
                    maxLength={20}
                    onValueChange={({ formattedValue }) => {
                      setFieldValue("bankAccountNumber", formattedValue);
                    }}
                  />
                </Row>
                {/* Bank Agency Number */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.bankAgencyNumber && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.bankAccountFarm.form.bankAgencyNumber}*
                  </label>
                </Row>
                <Row type="flex">
                  <NumberFormat
                    customInput={Input}
                    name="bankAgencyNumber"
                    allowLeadingZeros
                    value={values.bankAgencyNumber || ""}
                    placeholder={translation.defaultPlaceholder}
                    maxLength={10}
                    onValueChange={({ formattedValue }) => {
                      setFieldValue("bankAgencyNumber", formattedValue);
                    }}
                  />
                </Row>
                {/* Initial Balance */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.initialBalance && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.bankAccountFarm.form.initialBalance}*
                  </label>
                </Row>
                <Row type="flex">
                  <NumberFormat
                    customInput={Input}
                    name="initialBalance"
                    value={values.initialBalance || ""}
                    placeholder={translation.defaultPlaceholder}
                    allowNegative
                    decimalScale={2}
                    decimalSeparator=","
                    thousandSeparator="."
                    fixedDecimalScale={true}
                    addonBefore="R$"
                    onValueChange={({ floatValue }) => {
                      setFieldValue("initialBalance", floatValue);
                    }}
                  />
                </Row>
                {/* Initial Balance Date */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.initialBalanceDate && submitCount > 0
                        ? "error"
                        : ""
                    }
                  >
                    {translation.bankAccountFarm.form.initialBalanceDate}*
                  </label>
                </Row>
                <Row type="flex">
                  <DatePicker
                    name="initialBalanceDate"
                    format={"DD/MM/YYYY"}
                    value={
                      values.initialBalanceDate
                        ? moment(values.initialBalanceDate)
                        : null
                    }
                    disabledDate={(current) => current.isAfter(moment())}
                    placeholder={translation.defaultPlaceholder}
                    onChange={(date, dateString) => {
                      setFieldValue("initialBalanceDate", date);
                    }}
                  />
                </Row>
              </div>
              <Footer>
                <Row type="flex">
                  <Col span={24} className="buttonsDiv">
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={handleCloseDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>

                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default DrawerBankAccountFarm;
